import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import * as OT from '@opentok/client';
import Icon, { ICONS, COLORS } from '../../../general-components/icon';
import colors from '../../../../utils/colors';
import Popover from '../../../general-components/popover';
import {UnstyledUl} from './elements';
import { OpenTokContext } from '../../../../contexts/opentok/opentok-context';
import Sections from './sections';
import { GreenroomState as GS } from '../../../../stores/types/types';
import { showAlert } from '../../../general-components/alert-service';
import { setModalState } from '../../../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../../../stores/reducers/modal';
import { DeleteUserMedia } from '../../../../connection/media';
import { VirtualBg } from '../../../../stores/types/content';
import { loadVBGs, selectVBG } from '../../../../stores/actions/content';
import Preview from '../preview';
import useAddBackground from '../../../../hooks/virtual-backgrounds/use-add-background';
import { setShouldRepublishVideo } from '../../../../stores/actions/video';
import TooltipWrapper, { TTPosition } from '../../../general-components/tooltips/tooltip-wrapper';
import { IconDelete } from '@brandlive/pixel';

export const cleanUrl = (url: string, split: string) => {
	const fileType = url.split('.').pop();

	if (fileType === 'm3u8' || fileType === 'hls') {
		return `${url.split(`-${split}`).slice(0, -1).join('')}.${split}`;
	}

	return url;
};

export enum POP_TYPE {
	CLOSED = 'closed',
	ADD = 'add',
	UPLOADED = 'uploaded',
	DEFAULT = 'default',
}

const basePopData = {
	url: '', type: POP_TYPE.CLOSED, top: 0, left: 0, uniqueId: 'none',
};

interface Props {
	bespokePublisher?: OT.Publisher | null; // temp publisher for onboarding
	hidePreview?: boolean;
	hidePopover?: boolean;
}

export default function EffectsTab(props: Props) {
	const {
		bespokePublisher = null,
		hidePreview = false,
		hidePopover = false,
	} = props;

	const dispatch = useDispatch();
	const { token, user, userMeetingSettings: { r1080pUsers } } = useSelector((s: GS) => s.SessionReducer);
	const selectedVBG = useSelector((s: GS) => s.ContentReducer.selectedVBG);

	const userId = user?.gr_user ?? 0;

	const [popoverData, setPopoverData] = useState(basePopData);

	const vbgImageInput = React.useRef<HTMLInputElement>(null);

	const { opentok } = useContext(OpenTokContext);

	const resetPopover = () => {
		setPopoverData(basePopData);
	};

	const { uploadNewVBG } = useAddBackground(resetPopover);

	const onDownload = (url: string) => {
		const link = document.createElement('a');
		link.href = url;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		setPopoverData(basePopData);
	};

	const onDelete = (id: string) => {
		const numberId = parseInt(id.split('_')[1], 10);
		setPopoverData(basePopData);
		if (!token) return;
		DeleteUserMedia(token, numberId).then(() => {
			dispatch(loadVBGs(token, userId));
		}).catch((e) => {
			console.error(e);
			showAlert({
				message: 'Error deleting image',
				description: 'We ran into an issue deleting that image. Please try again.',
				duration: 4000,
			});
		});
	};

	const onUploadItem = () => {
		setPopoverData(basePopData);
		vbgImageInput.current?.click();
	};

	const onAddFromMediaLibrary = () => {
		dispatch(setModalState({
			modalTemplate: MODAL_TEMPLATES.MEDIA_LIBRARY,
			modalOpen: true,
			modalData: {},
		}));
		setPopoverData(basePopData);
	};

	const onPopoverOpened = useCallback((
		e: React.MouseEvent,
		type: POP_TYPE,
		uniqueId: string = 'none',
		url: string = '',
	) => {
		if (bespokePublisher || hidePopover) {
			// onboarding does not have popover but add card calls back to this
			onUploadItem();
		}
		e.preventDefault();
		e.stopPropagation();
		// user can add default bgs to their bgs
		setPopoverData({
			uniqueId,
			url,
			type,
			left: e.clientX - 140, // make sure popover doesnt go off screen
			top: e.clientY,
		});
	}, [bespokePublisher, hidePopover]);



	const selectCard = useCallback((nBackground: VirtualBg) => {
		dispatch(selectVBG(nBackground));
		opentok?.enableVirtualBackground(nBackground).catch(console.error);
	}, [opentok]);

	useEffect(() => {
		// the upload hook calls the opentok context but onboarding
		// has to use a useEffect to apply to the bespoke publisher instead.
		if (bespokePublisher && selectedVBG) {
			const { url, blur } = selectedVBG;
			try {
				if (blur) {
					bespokePublisher.applyVideoFilter({
						type: 'backgroundBlur',
						blurStrength: blur,
					});
				} else if (url) {
					bespokePublisher.applyVideoFilter({
						type: 'backgroundReplacement',
						backgroundImgUrl: url,
					});
				}
				// as this is onboarding we do not check 1080p
			} catch (e) {
				console.error(e);
			}
		}
	}, [bespokePublisher, selectedVBG?.url, selectedVBG?.blur]);

	const clearBackground = useCallback(() => {
		opentok?.disableVirtualBackground().catch(console.error);
		dispatch(selectVBG(null));
	}, [opentok]);

	const PopoverBuilder = (type: POP_TYPE, text: string) => {
		if (bespokePublisher || hidePopover) return null;

		let list;
		if (type === POP_TYPE.ADD) {
			// add card has easy options
			list = <UnstyledUl>
				<li className={classNames('select-option border-radius-none')} onClick={onUploadItem} style={{borderRadius: 0}}>
					<Icon name={ICONS.UPLOAD} size={16} color={COLORS.TEXT_GRAY_200} />
					Upload
				</li>
				<li className='select-option' onClick={onAddFromMediaLibrary}>
					<Icon name={ICONS.MEDIA_FILE} size={16} color={COLORS.TEXT_GRAY_200} />
					Media Library
				</li>
			</UnstyledUl>;
		} else {
			const isInUse = selectedVBG?.uniqueId === popoverData.uniqueId;
			list = <UnstyledUl>
				<li className='select-option' onClick={() => onDownload(popoverData.url)} style={{borderRadius: 0}}>
					<Icon name={ICONS.DOWNLOAD_ICON} size={16} color='white' />
					<span>Download</span>
				</li>
				<TooltipWrapper
					message={isInUse ? 'This background is currently in use. Switch to a different background before deleting.' : undefined}
					position={TTPosition.left}
				>
					<li
						className={classNames('select-option critical-option', { 'disabled': isInUse })}
						onClick={() => onDelete(popoverData.uniqueId)}
						style={{ borderTop: `1px solid ${colors.blGrayscale700}` }}
					>
						<Icon name={ICONS.TRASH} size={16} color={isInUse ? 'gray' : 'red'} />
						<span className={isInUse ? '' : 'text-warning'}>Delete</span>
					</li>
				</TooltipWrapper>
			</UnstyledUl>;
		}

		return <Popover
			position='right'
			open={popoverData.type === type}
			close={() => setPopoverData(basePopData)}
			classNameOfParent={`popover-parent ${popoverData.type}`}
			forcedStyles={{ position: 'fixed', top: popoverData.top, left: popoverData.left }}
		>
			<div className='options'>
				<div className='popover-header'>{text}</div>
				{list}
			</div>
		</Popover>;
	};

	useEffect(() => {
		if (token && user?.gr_user) {
			dispatch(loadVBGs(token, userId));
		}
	}, []);

	return (<>
		<input
			type="file"
			ref={vbgImageInput}
			style={{ display: 'none' }}
			onChange={(e) => {
				if (e.target.files && e.target.files[0]) {
					uploadNewVBG(e.target.files[0]);
					e.target.value = '';
				}
			}}
			accept=".jpeg, .png, .jpg, .gif"
		/>
		{bespokePublisher || hidePreview ? null : <Preview onClear={clearBackground} />}
		<Sections
			onSelect={selectCard}
			onClear={clearBackground}
			onPopover={onPopoverOpened}
			isOnboarding={!!bespokePublisher || hidePopover}
		/>
		{PopoverBuilder(POP_TYPE.ADD, 'ADD NEW')}
		{PopoverBuilder(POP_TYPE.UPLOADED, 'background image')}
		{PopoverBuilder(POP_TYPE.DEFAULT, 'background image')}
	</>);
}
